import { message } from 'antd';
import { useDispatch } from 'react-redux';
import {
  createContext, useContext, useEffect, useState,
} from 'react';

import { Action } from 'redux-saga';
import { useCategory } from '../../../../store/category';
import {
  categoryListAll,
  categoryListAllClear,
  categoryListAllErrorClear,
} from '../../../../store/category/listAll';
import {
  categoryStatistic as categoryStatisticAction,
  categoryStatisticClear as categoryStatisticClearAction,
  categoryStatisticErrorClear as categoryStatisticErrorClearAction,
} from '../../../../store/category/statistic';
import { getMessageInError } from '../../../../hooks/fetch';
import { useAuth } from '../../../../store/auth';
import { SubCategoryStatisticDisplayMode } from '../../../../store/subCategory';
import {
  CategoryByIdData,
  CategoryByIdSubCategory,
  categoryById,
  categoryByIdClear,
  categoryByIdErrorClear,
} from '../../../../store/category/byId';

const toLowercaseAndCompare = (first: string, second: string): boolean => {
  if (!first || !second) return false;

  const a = first.toLowerCase().trim();
  const b = second.toLowerCase().trim();

  return a.startsWith(b);
};

export const CATEGORY_QUERY_KEY = 'category';
export const SUB_CATEGORY_QUERY_KEY = 'subCategory';

interface CategoryContextProps {
  categoryId: string | null;
  setCategoryId: (id: string | null) => void;

  category: CategoryByIdData | null;

  subCategory: CategoryByIdSubCategory | null;
  setSubCategory: (subCategory: CategoryByIdSubCategory | null) => void;

  displayMode: SubCategoryStatisticDisplayMode;
  setDisplayMode: (mode: SubCategoryStatisticDisplayMode) => void;

  clearSelectedCategoryAndSubCategory: () => void;
}

const CategoryContext = createContext<CategoryContextProps | undefined>(
  undefined,
);

export const useCategoryContext = () => {
  const context = useContext(CategoryContext);

  if (!context) {
    throw new Error(
      'useCategoryContext must be used within a CategoryProvider',
    );
  }

  return context;
};

function CategoryProvider({ children }: React.PropsWithChildren) {
  const dispatch = useDispatch();

  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [category, setCategory] = useState<CategoryByIdData | null>(null);
  const [subCategory, setSubCategory] = useState<CategoryByIdSubCategory | null>(null);

  const [displayMode, setDisplayMode] = useState<SubCategoryStatisticDisplayMode>('percent');

  const { listAll, byId } = useCategory();
  const { user } = useAuth();

  useEffect(() => {
    dispatch(categoryListAll({ page: 1, pageSize: 100 }) as Action);

    return () => {
      dispatch(categoryListAllClear() as Action);
      dispatch(categoryListAllErrorClear() as Action);
    };
  }, [user]);

  useEffect(() => {
    if (listAll.error) {
      message
        .error(getMessageInError(listAll.error))
        .then(() => dispatch(categoryListAllErrorClear() as Action));
    }
  }, [listAll.error]);

  const clearSelectedCategoryAndSubCategory = () => {
    setCategory(null);
    setSubCategory(null);
  };

  useEffect(() => {
    if (!listAll.response?.data?.length || document.body.clientWidth <= 768) return;

    const defaultCategory = listAll.response.data
      .find(({ id }) => id === process.env.REACT_APP_DEFAULT_ACTIVE_CATEGORY_ID)
      || listAll.response.data.find(({ name }) => (
        toLowercaseAndCompare(name, process.env.REACT_APP_DEFAULT_ACTIVE_CATEGORY_NAME || '')))
      || listAll.response.data[0]
      || null;

    if (!defaultCategory) return;

    setCategoryId(defaultCategory.id);
  }, [listAll.response]);

  useEffect(() => {
    if (!categoryId) return;

    dispatch(categoryById({ id: categoryId }) as Action);

    // eslint-disable-next-line consistent-return
    return () => {
      dispatch(categoryByIdClear() as Action);
      dispatch(categoryByIdErrorClear() as Action);
    };
  }, [categoryId]);

  useEffect(() => {
    setCategory(byId.response || null);
  }, [byId.response]);

  useEffect(() => {
    if (byId.error) {
      message
        .error(getMessageInError(byId.error))
        .then(() => dispatch(categoryByIdErrorClear() as Action));
    }
  }, [byId.error]);

  useEffect(() => {
    if (!category) return;

    const defaultSubCategory = category.subCategories
      .find(({ id }) => id === process.env.REACT_APP_DEFAULT_ACTIVE_SUB_CATEGORY_ID)
      || category.subCategories.find(({ header }) => (
        toLowercaseAndCompare(header, process.env.REACT_APP_DEFAULT_ACTIVE_SUB_CATEGORY_NAME || '')))
      || category.subCategories[0]
      || null;

    setSubCategory(defaultSubCategory);
  }, [category]);

  return (
    <CategoryContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        category,
        categoryId,
        setCategoryId,
        subCategory,
        setSubCategory,
        displayMode,
        setDisplayMode,
        clearSelectedCategoryAndSubCategory,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
}

export default CategoryProvider;
